import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { UserContext } from '../context/UserContext';
import { platformRulesApi, PLATFORM_RULE_CATEGORIES } from '../api/platformRules';
import HrnbContainer, { HrnbRow, HrnbCol } from '../components/controls/HrnbContainer';
import HrnbCard from '../components/controls/HrnbCard';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbLoader from '../components/controls/HrnbLoader';
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import '../css/pages/RulesPage.css';

/**
 * Страница правил платформы
 * Интеграция: platformRulesApi.listPublic() / platformRulesApi.list()
 *
 * Категории (табы):
 *  - all — Все правила
 *  - COMMUNITY_GUIDELINES — Правила сообщества
 *  - TERMS_OF_SERVICE — Условия использования
 *  - CONTENT_POLICY — Политика контента
 *  - PRIVACY_POLICY — Политика конфиденциальности
 *  - CODE_OF_CONDUCT — Кодекс поведения
 */
const CATEGORY_TABS = [
    { value: 'all', label: 'Все правила' },
    ...Object.entries(PLATFORM_RULE_CATEGORIES).map(([value, label]) => ({ value, label })),
];

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

export default function RulesPage() {
    const { currentUser } = useContext(UserContext);
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');

    const fetchRules = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                size: 100,
                sortBy: 'sortOrder',
                direction: 'ASC',
            };
            // Authorised users see all rules, guests see only active ones
            const data = currentUser
                ? await platformRulesApi.list(params)
                : await platformRulesApi.listPublic(params);
            const rulesList = data?.content || [];
            setRules(rulesList);
        } catch (err) {
            setError(err.message || 'Ошибка при загрузке правил');
            setRules([]);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    // Filter by selected category (client-side, since we load everything at once)
    const visibleRules = activeTab === 'all'
        ? rules
        : rules.filter(r => r.category === activeTab);

    // When "all" tab is active, group rules by category for structured display
    const grouped = useMemo(() => {
        if (activeTab !== 'all') return null;
        const map = {};
        for (const rule of visibleRules) {
            if (!map[rule.category]) map[rule.category] = [];
            map[rule.category].push(rule);
        }
        // Preserve category order from PLATFORM_RULE_CATEGORIES
        return Object.keys(PLATFORM_RULE_CATEGORIES)
            .filter(cat => map[cat]?.length)
            .map(cat => ({
                category: cat,
                label: PLATFORM_RULE_CATEGORIES[cat],
                rules: map[cat],
            }));
    }, [visibleRules, activeTab]);

    const showInactiveNotice = currentUser && rules.some(r => !r.isActive);

    return (
        <HrnbContainer className="rules-page">
            <HrnbRow>
                <HrnbCol col={12}>
                    <div className="rules-page__header">
                        <h1 className="rules-page__title">Правила платформы</h1>
                        <p className="rules-page__description">
                            Ознакомьтесь с правилами сообщества, условиями использования,
                            политикой контента и другими документами платформы.
                        </p>
                    </div>
                </HrnbCol>
            </HrnbRow>

            <HrnbRow>
                <HrnbCol col={12}>
                    <HrnbTabs value={activeTab} onChange={setActiveTab} className="rules-page__tabs">
                        {CATEGORY_TABS.map(tab => (
                            <HrnbTab key={tab.value} label={tab.label} value={tab.value} />
                        ))}
                    </HrnbTabs>
                </HrnbCol>
            </HrnbRow>

            <HrnbRow>
                <HrnbCol col={12}>
                    {loading && (
                        <div className="rules-page__loading">
                            <HrnbLoader />
                        </div>
                    )}

                    {error && (
                        <HrnbAlert type="danger">{error}</HrnbAlert>
                    )}

                    {!loading && !error && visibleRules.length === 0 && (
                        <HrnbCard className="rules-page__empty">
                            <p>В этой категории правила пока не добавлены.</p>
                        </HrnbCard>
                    )}

                    {!loading && !error && showInactiveNotice && (
                        <div className="rules-page__notice">
                            Отображаются все правила, включая неактивные (доступно авторизованным пользователям).
                        </div>
                    )}

                    {!loading && !error && visibleRules.length > 0 && (
                        <div className="rules-page__list">
                            {/* "All rules" mode — grouped by category */}
                            {grouped && grouped.map(group => (
                                <div key={group.category} className="rules-page__group">
                                    <h2 className="rules-page__group-title">{group.label}</h2>
                                    {group.rules.map((rule, idx) => (
                                        <RuleItem key={rule.id} rule={rule} index={idx} />
                                    ))}
                                </div>
                            ))}

                            {/* Single category mode — flat list */}
                            {!grouped && visibleRules.map((rule, idx) => (
                                <RuleItem key={rule.id} rule={rule} index={idx} />
                            ))}
                        </div>
                    )}
                </HrnbCol>
            </HrnbRow>
        </HrnbContainer>
    );
}

/**
 * Single platform rule card
 */
function RuleItem({ rule, index }) {
    const { title, text, sortOrder, isActive, updatedAt, createdAt, categoryDisplayName } = rule;
    const date = formatDate(updatedAt || createdAt);

    return (
        <HrnbCard
            className={`rule-item${isActive === false ? ' rule-item--inactive' : ''}`}
            title={title || `Правило #${index + 1}`}
            subtitle={categoryDisplayName}
            actions={
                <span className="rule-item__order">#{sortOrder ?? index + 1}</span>
            }
        >
            <div className="rule-item__content">
                <p className="rule-item__text">{text}</p>
                {date && (
                    <div className="rule-item__meta">
                        <small>Обновлено: {date}</small>
                    </div>
                )}
                {isActive === false && (
                    <div className="rule-item__status">Неактивно</div>
                )}
            </div>
        </HrnbCard>
    );
}
